import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Dialog } from 'primereact/dialog';
import {
  FiSearch, FiFileText, FiClock, FiMessageSquare, FiRefreshCw, FiThumbsUp, FiThumbsDown,
  FiCheck, FiDownload, FiDollarSign as FiCreditCard, FiX, FiCopy,
} from 'react-icons/fi';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import MVRDetailsReport, {
  PersonDetails, AddressDetails, ViolationDetail, AccidentDetail, LicenseDetail,
} from './MVRDetailsReport';
import { useToast } from '../../context/ToastContext';
import '../../styles/QuoteProposal.css';
import '../../styles/Voice.css';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';

const PAGE_SIZE   = 10;
const getToken     = () => localStorage.getItem('toolboxToken') || '';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  Started: { label: 'Started', color: '#fbbf24' },
  OrderPlace: { label: 'OrderPlace', color: '#fbbf24' },
  CheckStatus: { label: 'CheckStatus', color: '#a78bfa' },
  Pending: { label: 'Pending', color: '#fbbf24' },
  Success: { label: 'Success', color: '#34d399' },
  Failed: { label: 'Failed', color: '#f87171' },
};

interface MVRTransaction {
  transactionId: string;
  orderId?: string;
  created: string;
  userName: string;
  apiType: 'ActivityHistory' | 'TransactionalMVR' | string;
  status: string;
  apiCost?: number;
  selectionOptionJson: string;
  mvrSuccessResponseJson?: string;
  isDriverAdded?: boolean;
  isViolationAdded?: boolean;
  totalCount?: number;
  _isCheckingStatus?: boolean;
}

const formatDateTime = (d: string) => {
  if (!d) return { date: '—', time: '' };
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return {
      date: `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`,
      time: dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
  } catch { return { date: d, time: '' }; }
};

const getSelectionOption = (row: MVRTransaction) => {
  try { return JSON.parse(row.selectionOptionJson || '{}'); } catch { return {}; }
};

const getClientName = (row: MVRTransaction): string => getSelectionOption(row).clientName || 'NA';

const getDriverName = (row: MVRTransaction): string => {
  const d = getSelectionOption(row).selectedDriverDetails;
  if (!d) return 'NA';
  const driver = Array.isArray(d) ? d[0] : d;
  if (!driver || (!driver.firstName && !driver.lastName)) return 'NA';
  return `${driver.firstName || ''} ${driver.lastName || ''}`.trim() || 'NA';
};

const MVRHistory: React.FC = () => {
  const navigate = useNavigate();
  const feedbackRef = useRef<HTMLDivElement>(null);
  const { showSuccess, showError } = useToast();

  const [rows, setRows]             = useState<MVRTransaction[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [checkingId, setCheckingId] = useState<string | null>(null);

  /* Preview */
  const [previewOpen, setPreviewOpen]   = useState(false);
  const [previewRow, setPreviewRow]     = useState<MVRTransaction | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfUrl, setPdfUrl]             = useState('');
  const [personDetails, setPersonDetails]   = useState<PersonDetails>({});
  const [addressDetails, setAddressDetails] = useState<AddressDetails>({});
  const [violations, setViolations]         = useState<ViolationDetail[]>([]);
  const [accidents, setAccidents]           = useState<AccidentDetail[]>([]);
  const [licenseInfo, setLicenseInfo]       = useState<LicenseDetail[]>([]);

  /* Feedback */
  const [feedbackTxId, setFeedbackTxId]     = useState<string | null>(null);
  const [feedbackText, setFeedbackText]     = useState('');
  const [feedbackRating, setFeedbackRating] = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor] = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError, setFeedBackError]   = useState('');

  /* Payment link */
  const [isSelectMode, setIsSelectMode]       = useState(false);
  const [selectedRows, setSelectedRows]       = useState<Set<string>>(new Set());
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentForm, setPaymentForm]         = useState({ firstName: '', lastName: '', email: '', contactNumber: '', agencyName: '', amount: '', description: '' });
  const [paymentErrors, setPaymentErrors]     = useState<Record<string, string>>({});
  const [createdPaymentUrl, setCreatedPaymentUrl] = useState('');

  const fetchData = useCallback(async (pageNum: number, signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ pageNumber: String(pageNum), recordCount: String(PAGE_SIZE) });
      const res = await fetch(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/mvr/history?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}`, accept: 'application/json' },
        signal,
      });
      if (res.status === 401) { navigate('/'); return; }
      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);
      const json = await res.json();
      const data: MVRTransaction[] = Array.isArray(json) ? json : (json.data ?? []);
      setRows(data);
      setTotalCount(data[0]?.totalCount ?? 0);
    } catch (e: any) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Failed to load MVR history');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

 useEffect(() => {
    let controller: AbortController | undefined;
    const timer = setTimeout(() => {
      controller = new AbortController();
      fetchData(page, controller.signal);
    }, 0);
    return () => { clearTimeout(timer); controller?.abort(); };
  }, [fetchData, page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.userName?.toLowerCase().includes(q) ||
      String(r.apiType).toLowerCase().includes(q) ||
      String(r.status).toLowerCase().includes(q),
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const checkStatus = useCallback((row: MVRTransaction) => {
    setCheckingId(row.transactionId);
    const isActivity = row.apiType === 'ActivityHistory';
    axios.get(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/mvr/${isActivity ? 'orderstatus' : 'mvrorderstatus'}`, {
      params: { orderId: row.orderId, transactionId: row.transactionId },
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        const status = res.data?.orderStatus;
        const fulfilled = isActivity ? status === 'fulfilled' : (status === 'fulfilled' || status === 'error');
        if (fulfilled) {
          const reportId = res.data?.links?.[0]?.id;
          axios.get(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/mvr/${isActivity ? 'report' : 'mvrreport'}`, {
            params: { reportId, transactionId: row.transactionId },
            headers: { Authorization: `Bearer ${getToken()}` },
          })
            .then(() => { setCheckingId(null); fetchData(page); })
            .catch(() => { setCheckingId(null); showError('Unknown error while getting the MVR report.'); });
        } else {
          setCheckingId(null);
        }
      })
      .catch((err: any) => {
        setCheckingId(null);
        if (err.response?.status === 401) navigate('/');
        else showError('Unknown error while checking MVR status.');
      });
  }, [navigate, page, fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewRow(null);
    setPdfUrl('');
    setPersonDetails({}); setAddressDetails({}); setViolations([]); setAccidents([]); setLicenseInfo([]);
  }, []);

  const onActivityHistoryPreview = (row: MVRTransaction) => {
    setPreviewOpen(true);
    try {
      const json = JSON.parse(row.mvrSuccessResponseJson || '{}');
      const person = json.publicRecord?.person || {};
      const address = json.reportOrder?.address || {};
      const violationRows: ViolationDetail[] = [];
      [...(json.publicRecord?.violations || []), ...(json.courtRecord?.violations || [])].forEach((item: any) => {
        (item.infractions || []).forEach((inf: any) => {
          violationRows.push({
            date: item.date, convictionDate: item.convictionDate, state: item.state,
            code: inf.stateCode, description: inf.description, location: item.location,
            speed: item.actualSpeed, acd: inf.acd, avd: inf.avd1,
          });
        });
      });
      const accidentRows: AccidentDetail[] = (json.publicRecord?.accidents || []).map((item: any) => ({
        date: item.date, location: item.location,
        code: item.infractions?.[0]?.stateCode, description: item.infractions?.[0]?.description,
        acd: item.infractions?.[0]?.acd, avd: item.infractions?.[0]?.avd1,
      }));
      const licenseClasses = json.publicRecord?.licenseClasses || {};
      const licenseRows: LicenseDetail[] = [];
      (['commercial', 'personal', 'identification', 'motorcycle'] as const).forEach(key => {
        const lic = licenseClasses[key]?.license;
        if (lic) {
          licenseRows.push({
            issueDate: lic.issueDate, expirationDate: lic.expirationDate, class: lic.classCode,
            classDescription: lic.classDescription, status: lic.statusList?.[0]?.name,
            restrictions: lic.restrictions?.[0]?.description, cdlMedicalType: lic.cdlMedicalType,
            cdlMedicalExpirationDate: lic.cdlMedicalExpirationDate,
            type: key.charAt(0).toUpperCase() + key.slice(1),
          });
        }
      });
      setPersonDetails(person);
      setAddressDetails(address);
      setViolations(violationRows);
      setAccidents(accidentRows);
      setLicenseInfo(licenseRows);
    } catch { /* malformed response — show empty report */ }
  };

  const onTransactionalPreview = (row: MVRTransaction) => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPdfUrl('');
    axios.post(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/mvr/previewdocument/${row.transactionId}`, {}, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => { setPdfUrl(res.data?.fileUrl || ''); setPreviewLoading(false); })
      .catch((err: any) => {
        setPreviewLoading(false);
        if (err.response?.status === 401) navigate('/');
        else showError('Unknown error while getting the MVR report PDF.');
      });
  };

  const onPreviewClick = (row: MVRTransaction) => {
    setPreviewRow(row);
    setPreviewLoading(row.apiType !== 'ActivityHistory');
    if (row.apiType === 'ActivityHistory') onActivityHistoryPreview(row);
    else onTransactionalPreview(row);
  };

  const onDownload = useCallback((row: MVRTransaction) => {
    axios.get(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/mvr/download`, {
      params: { transactionId: row.transactionId },
      responseType: 'blob',
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(res => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'MVR_Report.pdf';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err: any) => {
        if (err.response?.status === 401) navigate('/');
        else showError('Unknown error while downloading the MVR report.');
      });
  }, [navigate]);

  /* ── Feedback ── */
  const openFeedback = useCallback((row: MVRTransaction, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackTxId(row.transactionId);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackTxId(null); setFeedbackAnchor(null); setFeedbackText(''); setFeedbackRating(null); setFeedBackError('');
  }, []);

  useEffect(() => {
    if (!feedbackTxId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackTxId, closeFeedback]);

  const saveRating = useCallback((txId: string, isLiked: boolean | null, feedback: string) => {
    axios.post(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/mvr/isliked`, { TransactionId: txId, IsLiked: isLiked, Feedback: feedback ?? '' }, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(() => { showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch((err: any) => { if (err.response?.status === 401) navigate('/'); });
  }, [navigate, closeFeedback]);

  const onThumbsUpClick = useCallback(() => {
    if (!feedbackTxId) return;
    saveRating(feedbackTxId, feedbackRating === true ? null : true, feedbackText);
  }, [feedbackTxId, feedbackRating, feedbackText, saveRating]);

  const onThumbsDownClick = useCallback(() => {
    if (!feedbackTxId) return;
    saveRating(feedbackTxId, feedbackRating === false ? null : false, feedbackText);
  }, [feedbackTxId, feedbackRating, feedbackText, saveRating]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackTxId) return;
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please provide your feedback within 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(`${(process.env.REACT_APP_DataFabric_Url      || '')}/api/mvr/feedback`, { TransactionId: feedbackTxId, Feedback: feedbackText }, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch((err: any) => { setFeedbackSubmitting(false); if (err.response?.status === 401) navigate('/'); });
  }, [feedbackTxId, feedbackText, navigate, closeFeedback]);

  /* ── Payment link ── */
  const toggleRowSelected = (txId: string) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(txId)) next.delete(txId); else next.add(txId);
      return next;
    });
  };

  const onCreateLinkClick = () => {
    const chosen = filtered.filter(r => selectedRows.has(r.transactionId));
    if (chosen.length === 0) { showError('Please select at least one row.'); return; }
    const total = chosen.reduce((sum, r) => sum + (r.apiCost ?? 0) / 100, 0);
    const desc = chosen.map(r => `${getClientName(r)} - ${getDriverName(r)} - $${((r.apiCost ?? 0) / 100).toFixed(2)}`).join('\n');
    setPaymentForm({ firstName: '', lastName: '', email: '', contactNumber: '', agencyName: '', amount: total.toFixed(2), description: desc });
    setPaymentErrors({});
    setShowPaymentDialog(true);
  };

  const validatePayment = () => {
    const e: Record<string, string> = {};
    if (!paymentForm.firstName.trim()) e.firstName = 'Required';
    if (!paymentForm.lastName.trim()) e.lastName = 'Required';
    if (!paymentForm.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paymentForm.email)) e.email = 'Invalid email';
    if (!paymentForm.contactNumber.trim()) e.contactNumber = 'Required';
    if (!paymentForm.agencyName.trim()) e.agencyName = 'Required';
    if (!paymentForm.amount.trim()) e.amount = 'Required';
    else if (isNaN(Number(paymentForm.amount)) || Number(paymentForm.amount) <= 0) e.amount = 'Must be a positive number';
    setPaymentErrors(e);
    return Object.keys(e).length === 0;
  };

  const onPaymentSubmit = async () => {
    if (!validatePayment()) return;
    setPaymentSubmitting(true);
    try {
      const res = await axios.post(`${(process.env.REACT_APP_STRIPEPAYMENTGATEWAY || '')}/api/Billing/create-checkout-session`, {
        email: paymentForm.email, amount: Number(paymentForm.amount), agencyName: paymentForm.agencyName,
        firstName: paymentForm.firstName, lastName: paymentForm.lastName,
        contactNumber: paymentForm.contactNumber, description: paymentForm.description,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (res.data?.success) setCreatedPaymentUrl(res.data.paymentLink);
      else showError(res.data?.errorMessage || 'Failed to create payment link.');
    } catch (err: any) {
      if (err.response?.status === 401) navigate('/');
      else showError('Something went wrong while creating the payment link.');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const closePaymentDialog = () => {
    setShowPaymentDialog(false); setCreatedPaymentUrl(''); setPaymentErrors({});
    setIsSelectMode(false); setSelectedRows(new Set());
  };

  return (
    <>
      <motion.div className="qp-history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <div className="qp-history__header">
          <h3 className="qp-history__title">MVR History</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="qp-history__search">
              <FiSearch size={13} />
              <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            {isSelectMode ? (
              <>
                <motion.button className="csh-refresh-btn" onClick={onCreateLinkClick} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <FiCreditCard size={13} /> Create Link
                </motion.button>
                <motion.button className="csh-refresh-btn" onClick={() => { setIsSelectMode(false); setSelectedRows(new Set()); }} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <FiX size={13} /> Cancel
                </motion.button>
              </>
            ) : (
              <motion.button className="csh-refresh-btn" onClick={() => setIsSelectMode(true)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <FiCreditCard size={13} /> Payment Link
              </motion.button>
            )}
            <motion.button className="csh-refresh-btn" onClick={() => fetchData(page)} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} /> Refresh
            </motion.button>
          </div>
        </div>

        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state" style={{ color: 'var(--accent-red)' }}>
              <FiFileText size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
           <div className="qp-history-loader" style={{ minHeight: 280 }}>
                       <div className="qp-history-loader__orb">
                         <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                         <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                         <div className="qp-history-loader__core"><FiClock size={16} /></div>
                       </div>
                       <div className="qp-history-loader__text">
                         <p className="qp-history-loader__title">Loading History</p>
                         <p className="qp-history-loader__sub">
                           Fetching MVR history 
                           <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                         </p>
                       </div>
                     </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state"><FiFileText size={28} /><p>{search ? 'No records match your search' : 'No MVR requests yet.'}</p></div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    {isSelectMode && <th className="qp-table__th" style={{ width: 36 }} />}
                    <th className="qp-table__th">Submitted Date</th>
                    <th className="qp-table__th">UserName</th>
                    <th className="qp-table__th">Client Name</th>
                    <th className="qp-table__th">Driver Name</th>
                    <th className="qp-table__th">API Type</th>
                    <th className="qp-table__th">Status</th>
                    <th className="qp-table__th">API Cost</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Driver In AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Violation In AMS</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Action</th>
                    <th className="qp-table__th" style={{ textAlign: 'center' }}>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => {
                    const { date, time } = formatDateTime(row.created);
                    const canCheck = row.status === 'OrderPlace' || row.status === 'Pending';
                    const canPreview = row.status === 'Success';
                    return (
                      <tr key={row.transactionId ?? i} className="qp-table__row">
                        {isSelectMode && (
                          <td className="qp-table__td">
                            <input type="checkbox" checked={selectedRows.has(row.transactionId)} onChange={() => toggleRowSelected(row.transactionId)} />
                          </td>
                        )}
                        <td className="qp-table__td" style={{ whiteSpace: 'nowrap' }}>{date}<div style={{ fontSize: 10, opacity: 0.6 }}>{time}</div></td>
                        <td className="qp-table__td">{row.userName}</td>
                        <td className="qp-table__td">{getClientName(row)}</td>
                        <td className="qp-table__td">{getDriverName(row)}</td>
                        <td className="qp-table__td">{row.apiType === 'ActivityHistory' ? 'Activity History' : row.apiType === 'TransactionalMVR' ? 'Transactional MVR' : 'NA'}</td>
                        <td className="qp-table__td">
                          {STATUS_CONFIG[row.status] && (
                            <span className="qp-status-pill" style={{ color: STATUS_CONFIG[row.status].color }}>
                              {STATUS_CONFIG[row.status].label}
                            </span>
                          )}
                        </td>
                        <td className="qp-table__td">${((row.apiCost ?? 0) / 100).toFixed(2)}</td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>{row.isDriverAdded ? 'Yes' : 'No'}</td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>{row.isViolationAdded ? 'Yes' : 'No'}</td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          {canCheck ? (
                            checkingId === row.transactionId
                              ? <FiRefreshCw size={14} className="ho-spin" />
                              : (
                                <button className="qp-table__draft-link" onClick={() => checkStatus(row)} style={{ whiteSpace: 'nowrap' }}>
                                  Check Status
                                </button>
                              )
                          ) : canPreview ? (
                            <button className="qp-table__action-btn" title="Preview" onClick={() => onPreviewClick(row)} style={{ margin: '0 auto' }}>
                              <img src={PreviewIcon} alt="Preview" width={20} height={20} />
                            </button>
                          ) : (
                            <span className="text-12 quote-not-span">Not Available</span>
                          )}
                        </td>
                        <td className="qp-table__td" style={{ textAlign: 'center' }}>
                          <button className="qp-table__action-btn" title="Feedback" onClick={e => openFeedback(row, e)} style={{ margin: '0 auto' }}>
                            <FiMessageSquare size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}</span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                <span className="qp-pagination__btn qp-pagination__btn--active">{page}</span>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback popup ── */}
      {feedbackTxId && feedbackAnchor && (
        <div ref={feedbackRef} className="csfb-popup" style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}>
          <h4 className="csfb-title">Feedback</h4>
          <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={e => setFeedbackText(e.target.value)} rows={4} />
          <div className="csfb-rating">
            <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUpClick} title="Thumbs up"><FiThumbsUp size={20} /></button>
            <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDownClick} title="Thumbs down"><FiThumbsDown size={20} /></button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedback}>{feedbackSubmitting ? 'Submitting…' : 'Submit'}</button>
          </div>
        </div>
      )}

      {/* ── Preview dialog ── */}
      {previewOpen && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(8, 6, 20, 0.92)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={closePreview}
        >
          <div
            className="ho-modal"
            style={{
              width: previewRow?.apiType === 'ActivityHistory' ? 'min(1200px, 92vw)' : 'min(1000px, 90vw)',
              height: '86vh',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <FiFileText size={16} className="ho-modal__icon" />
                <h3 className="ho-modal__title">{previewRow?.apiType === 'ActivityHistory' ? 'MVR Report' : 'MVR Document'}</h3>
              </div>
              <button className="ho-modal__close" onClick={closePreview}><FiX size={16} /></button>
            </div>
            <div
              className="ho-modal__body"
              style={{
                padding: previewRow?.apiType === 'ActivityHistory' ? '20px 22px' : 0,
                display: 'flex', flexDirection: 'column',
              }}
            >
              {previewLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}><FiRefreshCw size={20} className="ho-spin" /></div>
              ) : previewRow?.apiType === 'ActivityHistory' ? (
                <MVRDetailsReport
                  transactionId={previewRow.transactionId}
                  clientId={getSelectionOption(previewRow).clientId || ''}
                  clientName={getClientName(previewRow)}
                  personDetails={personDetails}
                  addressDetails={addressDetails}
                  violationsDetails={violations}
                  accidentsDetails={accidents}
                  licenseDetails={licenseInfo}
                  isDriverAdded={!!previewRow.isDriverAdded}
                  isViolationAdded={!!previewRow.isViolationAdded}
                  onWriteBackUpdated={(driverAdded, violationAdded) => {
                    setRows(prev => prev.map(r => r.transactionId === previewRow.transactionId ? { ...r, isDriverAdded: driverAdded, isViolationAdded: violationAdded } : r));
                    setPreviewRow(prev => prev ? { ...prev, isDriverAdded: driverAdded, isViolationAdded: violationAdded } : prev);
                  }}
                />
              ) : (
                <>
                  {previewRow && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 14px' }}>
                      <button className="qp-table__action-btn" title="Download" onClick={() => onDownload(previewRow)}><FiDownload size={16} /></button>
                    </div>
                  )}
                  {pdfUrl ? (
                    <iframe src={`${pdfUrl}#zoom=90`} title="MVR PDF Preview" style={{ width: '100%', flex: 1, border: 'none' }} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--text-tertiary)' }}>PDF not available</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* ── Payment link dialog ── */}
      {showPaymentDialog && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(8, 6, 20, 0.92)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={() => { if (!paymentSubmitting) closePaymentDialog(); }}
        >
          <div className="ho-modal" style={{ width: 'min(580px, 92vw)', maxHeight: '88vh' }} onClick={e => e.stopPropagation()}>
            <div className="ho-modal__header">
              <div className="ho-modal__title-row">
                <FiCreditCard size={16} className="ho-modal__icon" />
                <h3 className="ho-modal__title">Create Payment Link</h3>
              </div>
              <button className="ho-modal__close" onClick={() => { if (!paymentSubmitting) closePaymentDialog(); }}><FiX size={16} /></button>
            </div>

            <div className="ho-modal__body">
              {createdPaymentUrl ? (
                <div style={{ padding: 12, textAlign: 'center' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#4ED56E,#00966E)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <FiCheck size={22} style={{ color: '#fff' }} />
                  </div>
                  <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: 'var(--text-primary)' }}>Payment link created successfully!</p>
                  <div style={{ background: 'var(--surface-glass)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, border: '1px solid var(--border-glass)' }}>
                    <a href={createdPaymentUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', color: 'var(--accent-purple)', fontSize: 12 }}>{createdPaymentUrl}</a>
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button className="qp-table__action-btn" onClick={() => { navigator.clipboard.writeText(createdPaymentUrl); showSuccess('Payment link copied to clipboard.'); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', padding: '6px 14px', color: 'var(--text-primary)' }}>
                      <FiCopy size={14} /> Copy Link
                    </button>
                    <button className="cs-browse-btn" onClick={closePaymentDialog}>Done</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div className="ho-form-grid">
                    <div className="ho-form-field"><label className="ho-form-label">First Name</label><input className="ho-input" value={paymentForm.firstName} onChange={e => setPaymentForm(p => ({ ...p, firstName: e.target.value }))} />{paymentErrors.firstName && <span className="ho-field-error">{paymentErrors.firstName}</span>}</div>
                    <div className="ho-form-field"><label className="ho-form-label">Last Name</label><input className="ho-input" value={paymentForm.lastName} onChange={e => setPaymentForm(p => ({ ...p, lastName: e.target.value }))} />{paymentErrors.lastName && <span className="ho-field-error">{paymentErrors.lastName}</span>}</div>
                    <div className="ho-form-field"><label className="ho-form-label">Email</label><input className="ho-input" value={paymentForm.email} onChange={e => setPaymentForm(p => ({ ...p, email: e.target.value }))} />{paymentErrors.email && <span className="ho-field-error">{paymentErrors.email}</span>}</div>
                    <div className="ho-form-field"><label className="ho-form-label">Contact Number</label><input className="ho-input" value={paymentForm.contactNumber} onChange={e => setPaymentForm(p => ({ ...p, contactNumber: e.target.value }))} />{paymentErrors.contactNumber && <span className="ho-field-error">{paymentErrors.contactNumber}</span>}</div>
                    <div className="ho-form-field"><label className="ho-form-label">Agency Name</label><input className="ho-input" value={paymentForm.agencyName} onChange={e => setPaymentForm(p => ({ ...p, agencyName: e.target.value }))} />{paymentErrors.agencyName && <span className="ho-field-error">{paymentErrors.agencyName}</span>}</div>
                    <div className="ho-form-field"><label className="ho-form-label">Amount (USD)</label><input className="ho-input" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} />{paymentErrors.amount && <span className="ho-field-error">{paymentErrors.amount}</span>}</div>
                  </div>
                  <div className="ho-form-field">
                    <label className="ho-form-label">Description</label>
                    <textarea className="ho-input" rows={5} value={paymentForm.description} onChange={e => setPaymentForm(p => ({ ...p, description: e.target.value }))} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              )}
            </div>

            {!createdPaymentUrl && (
              <div className="ho-modal__footer" style={{ justifyContent: 'flex-end' }}>
                <button onClick={closePaymentDialog} disabled={paymentSubmitting} style={{ padding: '8px 20px', fontSize: 13, fontWeight: 600, background: 'var(--surface-glass)', color: 'var(--text-secondary)', border: '1px solid var(--border-glass)', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                <button className="cs-browse-btn" disabled={paymentSubmitting} onClick={onPaymentSubmit}>{paymentSubmitting ? 'Creating…' : 'Create Link'}</button>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default MVRHistory;

